import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

type FeaturedSliderProps = {
  items: Movie[];
};

export default function FeaturedSlider({ items }: FeaturedSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);

  const scrollBy = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.9;
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const timer = window.setInterval(() => {
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 8) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: el.clientWidth * 0.9, behavior: 'smooth' });
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-widest text-accent-600">
            Featured
          </span>
          <h2 className="mt-1 text-xl font-bold text-foreground-950 md:text-2xl">精选推荐</h2>
          <p className="mt-1.5 text-sm text-foreground-600">
            值得一看的代表作品，点击海报进入详情了解剧情与制作资料
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="上一组"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="下一组"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="bh-scrollbar mt-6 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 md:gap-5"
      >
        {items.map((movie) => (
          <Link
            key={movie.id}
            to={`/detail/${movie.id}`}
            className="group relative w-[260px] shrink-0 snap-start overflow-hidden rounded-lg bg-foreground-950 md:w-[300px]"
          >
            <div className="aspect-[16/10] w-full overflow-hidden">
              <img
                src={movie.poster}
                alt={`${movie.title} 电影剧照`}
                title={`${movie.title} - 北条麻妃电影`}
                className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/20 to-transparent"></div>
            <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
              <div className="flex flex-wrap items-center gap-1.5">
                {movie.genres.slice(0, 2).map((g) => (
                  <span
                    key={g}
                    className="rounded-full bg-accent-500/90 px-2 py-0.5 text-[11px] font-medium text-background-50"
                  >
                    {g}
                  </span>
                ))}
              </div>
              <h3 className="mt-2 text-lg font-bold text-background-50">{movie.title}</h3>
              <p className="mt-1 flex items-center gap-2 text-xs text-background-100/85">
                <span>{movie.year}</span>
                <span className="h-3 w-px bg-background-100/40"></span>
                <span>{movie.director} 导演</span>
                <span className="h-3 w-px bg-background-100/40"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-300"></i>
                  {movie.rating.toFixed(1)}
                </span>
              </p>
              <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-background-100/80">
                {movie.tagline}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}